import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { ConnectivityIndicator } from "./ConnectivityIndicator";
import { ThemeToggle } from "./ThemeToggle";
import { LanguageToggle } from "./LanguageToggle";
import { useDiscardGuard } from "./discardGuard";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";

/**
 * The account section shared by the phone bottom sheet and the tablet/desktop
 * account menu (spec §7). Reuses the existing theme, language and login logic;
 * connectivity is part of the account section as well as the offline banner.
 */
export function AccountPanel({ onNavigate }: { onNavigate?: () => void }) {
  const t = useT();
  const user = useAppStore((s) => s.user);
  const logout = useAppStore((s) => s.logout);
  const guard = useDiscardGuard();

  return (
    <Box sx={{ p: 2, width: "100%", maxWidth: 320 }} data-testid="account-panel">
      <Typography variant="subtitle2">{user?.displayName ?? user?.sub ?? t("profile")}</Typography>
      {user?.sub && user?.sub !== user?.displayName && (
        <Typography variant="body2" color="text.secondary">
          {user.sub}
        </Typography>
      )}
      {user?.email && (
        <Typography variant="body2" color="text.secondary">
          {user.email}
        </Typography>
      )}

      <Box sx={{ my: 1 }}>
        <ConnectivityIndicator />
      </Box>

      <Divider sx={{ my: 1.5 }} />
      <ThemeToggle />
      <Divider sx={{ my: 1.5 }} />
      <Stack direction="row" justifyContent="center">
        <LanguageToggle />
      </Stack>
      <Divider sx={{ my: 1.5 }} />
      <Button
        fullWidth
        size="small"
        color="inherit"
        onClick={() => {
          // Logout is an exit from an edit too. Close the sheet/menu first so
          // the discard prompt is not stacked under its overlay, then ask.
          onNavigate?.();
          guard.request(() => void logout());
        }}
      >
        {t("logout")}
      </Button>
    </Box>
  );
}
